import { useContext } from 'react'

import { digObject } from '@domain-group/javascript-utils'

import {
  Button, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import integrationPlatforms from '@functions/integrationPlatform'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useSystem, { useSystemForm } from '@hooks/useSystem'

import IntegrationPlatformListItem from './IntegrationPlatformListItem'

const MODAL_KEY = 'ManageSystemIntegrationsModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateSystem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageSystemIntegrationsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateSystem } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { system } = modalPayload

  const {
    callbacks: { updateSystem: updateFn },
    updating,
  } = useSystem(system)

  const systemForm = useSystemForm(system, { validateOn: 'change' })
  const { entityState } = systemForm

  const updateSystemPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'System Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Broadcast" titleSecondLine="API Calls" />

      <SidebarModal.Content>
        {integrationPlatforms.map(integrationPlatform => (
          <IntegrationPlatformListItem
            integrationPlatform={integrationPlatform}
            key={integrationPlatform.key}
            systemForm={systemForm}
          />
        ))}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateSystem(updateSystemPayload)}
          size="large"
        >
          Update System
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageSystemIntegrationsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
